import React, { useState, useEffect } from "react";
import axios from "axios";
import Sidebar from "./Sidebar";
import "./adminRoutes.css";

const inrFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0
});

export default function AdminPickupRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("ALL"); // ALL | PENDING | APPROVED | REJECTED | COMPLETED
  const [searchTerm, setSearchTerm] = useState("");

  // Review Modal State
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [showRejectBox, setShowRejectBox] = useState(false);

  const [feedback, setFeedback] = useState({ type: "", msg: "" });

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`/api/admin/fulfillment/pickup-requests?status=${activeTab}`, {
        withCredentials: true
      });
      if (res.data?.pickupRequests) {
        setRequests(res.data.pickupRequests);
      }
    } catch (err) {
      setFeedback({ type: "error", msg: err.response?.data?.msg || "Failed to load pickup requests." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleApprove = async (id) => {
    try {
      setActionLoading(true);
      const res = await axios.put(`/api/admin/fulfillment/pickup-requests/${id}/approve`, {}, { withCredentials: true });
      setFeedback({ type: "success", msg: res.data?.message || "Pickup request approved successfully." });
      setReviewModalOpen(false);
      setSelectedRequest(null);
      fetchRequests();
    } catch (err) {
      alert(err.response?.data?.msg || "Failed to approve pickup request.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (id) => {
    if (!rejectionReason.trim()) {
      return alert("Please enter a reason for rejecting this pickup request.");
    }

    try {
      setActionLoading(true);
      const res = await axios.put(
        `/api/admin/fulfillment/pickup-requests/${id}/reject`,
        { reason: rejectionReason.trim() },
        { withCredentials: true }
      );
      setFeedback({ type: "success", msg: res.data?.message || "Pickup request rejected." });
      setReviewModalOpen(false);
      setSelectedRequest(null);
      setRejectionReason("");
      setShowRejectBox(false);
      fetchRequests();
    } catch (err) {
      alert(err.response?.data?.msg || "Failed to reject pickup request.");
    } finally {
      setActionLoading(false);
    }
  };

  const filtered = requests.filter((r) => {
    const term = searchTerm.toLowerCase();
    return (
      r.orderNumber?.toLowerCase().includes(term) ||
      r.pickupRequestId?.toLowerCase().includes(term) ||
      r.customerName?.toLowerCase().includes(term) ||
      r.vendor?.firstName?.toLowerCase().includes(term) ||
      r.vendor?.lastName?.toLowerCase().includes(term) ||
      r.store?.name?.toLowerCase().includes(term)
    );
  });

  const pendingCount = requests.filter(r => r.status === "PENDING").length;
  const approvedCount = requests.filter(r => r.status === "APPROVED").length;
  const rejectedCount = requests.filter(r => r.status === "REJECTED").length;

  return (
    <div className="dashboard">
      <Sidebar />
      <div className="dashboardContainer" style={{ padding: "28px", background: "#f8fafc", minHeight: "100vh" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px", marginBottom: "24px" }}>
          <div>
            <h1 style={{ fontSize: "24px", fontWeight: "900", color: "#0f172a", margin: "0 0 4px" }}>
              🚚 Vendor Order Pickup Requests
            </h1>
            <p style={{ fontSize: "14px", color: "#64748b", margin: 0 }}>
              Review vendor pickup requests, verify warehouse/store locations, and assign courier logistics.
            </p>
          </div>

          <button
            type="button"
            onClick={fetchRequests}
            className="btn btn-outline-primary"
            style={{ fontSize: "13px", fontWeight: "700", padding: "8px 16px" }}
          >
            ↻ Refresh Requests
          </button>
        </div>

        {/* Feedback Alert */}
        {feedback.msg && (
          <div
            style={{
              background: feedback.type === "success" ? "#f0fdf4" : "#fef2f2",
              border: `1.5px solid ${feedback.type === "success" ? "#bbf7d0" : "#fecaca"}`,
              color: feedback.type === "success" ? "#166534" : "#991b1b",
              padding: "12px 18px",
              borderRadius: "10px",
              marginBottom: "20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: "13.5px",
              fontWeight: "700"
            }}
          >
            <span>{feedback.msg}</span>
            <button type="button" onClick={() => setFeedback({ type: "", msg: "" })} style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", fontSize: "16px" }}>✕</button>
          </div>
        )}

        {/* Top Summary KPI Cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px", marginBottom: "24px" }}>
          <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "16px", boxShadow: "0 2px 6px rgba(0,0,0,0.02)" }}>
            <span style={{ fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase" }}>Total Requests</span>
            <div style={{ fontSize: "22px", fontWeight: "900", color: "#0f172a", marginTop: "4px" }}>{requests.length}</div>
          </div>
          <div style={{ background: "#ffffff", border: "1.5px solid #fef3c7", borderRadius: "12px", padding: "16px", boxShadow: "0 2px 6px rgba(0,0,0,0.02)" }}>
            <span style={{ fontSize: "11px", fontWeight: "800", color: "#b45309", textTransform: "uppercase" }}>● Pending Review</span>
            <div style={{ fontSize: "22px", fontWeight: "900", color: "#b45309", marginTop: "4px" }}>{pendingCount}</div>
          </div>
          <div style={{ background: "#ffffff", border: "1.5px solid #dcfce7", borderRadius: "12px", padding: "16px", boxShadow: "0 2px 6px rgba(0,0,0,0.02)" }}>
            <span style={{ fontSize: "11px", fontWeight: "800", color: "#166534", textTransform: "uppercase" }}>✓ Approved Pickups</span>
            <div style={{ fontSize: "22px", fontWeight: "900", color: "#16a34a", marginTop: "4px" }}>{approvedCount}</div>
          </div>
          <div style={{ background: "#ffffff", border: "1.5px solid #fee2e2", borderRadius: "12px", padding: "16px", boxShadow: "0 2px 6px rgba(0,0,0,0.02)" }}>
            <span style={{ fontSize: "11px", fontWeight: "800", color: "#991b1b", textTransform: "uppercase" }}>✕ Rejected</span>
            <div style={{ fontSize: "22px", fontWeight: "900", color: "#dc2626", marginTop: "4px" }}>{rejectedCount}</div>
          </div>
        </div>

        {/* Filter Controls & Search */}
        <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "14px", padding: "18px 22px", marginBottom: "20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "14px" }}>
          {/* Status Tabs */}
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {["ALL", "PENDING", "APPROVED", "REJECTED"].map((st) => (
              <button
                key={st}
                type="button"
                onClick={() => setActiveTab(st)}
                style={{
                  padding: "6px 14px",
                  fontSize: "12.5px",
                  fontWeight: "800",
                  borderRadius: "8px",
                  border: "none",
                  background: activeTab === st ? "#0f172a" : "#f1f5f9",
                  color: activeTab === st ? "#ffffff" : "#475569",
                  cursor: "pointer"
                }}
              >
                {st === "ALL" ? "All Requests" : st === "PENDING" ? "⏳ Pending" : st === "APPROVED" ? "✓ Approved" : "✕ Rejected"}
              </button>
            ))}
          </div>

          {/* Search Box */}
          <div style={{ minWidth: "260px" }}>
            <input
              type="text"
              placeholder="Search by Order #, Vendor, Customer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: "100%", padding: "8px 12px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "13px" }}
            />
          </div>
        </div>

        {/* Requests Table */}
        <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "14px", padding: "20px", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
          {loading ? (
            <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>Loading pickup requests...</div>
          ) : filtered.length > 0 ? (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <thead>
                  <tr style={{ borderBottom: "1.5px solid #edf0f4", color: "#64748b", fontSize: "11px", fontWeight: "800", textTransform: "uppercase" }}>
                    <th style={{ padding: "10px 8px", textAlign: "left" }}>REQUEST ID / DATE</th>
                    <th style={{ padding: "10px 8px", textAlign: "left" }}>ORDER #</th>
                    <th style={{ padding: "10px 8px", textAlign: "left" }}>VENDOR &amp; STORE</th>
                    <th style={{ padding: "10px 8px", textAlign: "left" }}>PICKUP LOCATION</th>
                    <th style={{ padding: "10px 8px", textAlign: "right" }}>AMOUNT</th>
                    <th style={{ padding: "10px 8px", textAlign: "left" }}>STATUS</th>
                    <th style={{ padding: "10px 8px", textAlign: "right" }}>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((req) => (
                    <tr key={req._id} style={{ borderBottom: "1px solid #edf0f4" }}>
                      <td style={{ padding: "12px 8px" }}>
                        <span style={{ fontWeight: "800", color: "#0f172a", display: "block" }}>{req.pickupRequestId}</span>
                        <span style={{ fontSize: "11px", color: "#64748b" }}>
                          {new Date(req.requestedAt || req.createdAt).toLocaleString("en-IN", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </td>
                      <td style={{ padding: "12px 8px" }}>
                        <strong style={{ color: "#1e40af" }}>#{req.orderNumber}</strong>
                        <span style={{ display: "block", fontSize: "11.5px", color: "#64748b" }}>Cust: {req.customerName}</span>
                      </td>
                      <td style={{ padding: "12px 8px" }}>
                        <strong style={{ color: "#0f172a" }}>{req.store?.name || req.vendor?.businessName || "Vendor"}</strong>
                        <span style={{ display: "block", fontSize: "11px", color: "#64748b" }}>
                          {req.vendor?.firstName} {req.vendor?.lastName} ({req.vendor?.number || req.vendor?.phone || ""})
                        </span>
                      </td>
                      <td style={{ padding: "12px 8px" }}>
                        <strong style={{ color: "#0f172a" }}>{req.pickupLocationSnapshot?.name || "Store Hub"}</strong>
                        <span style={{ display: "block", fontSize: "11.5px", color: "#64748b" }}>
                          📍 {req.pickupLocationSnapshot?.street}, {req.pickupLocationSnapshot?.city}
                        </span>
                        <span style={{ display: "block", fontSize: "11px", color: "#475569" }}>
                          👤 {req.pickupLocationSnapshot?.contactPerson} ({req.pickupLocationSnapshot?.contactPhone})
                        </span>
                      </td>
                      <td style={{ padding: "12px 8px", textAlign: "right", fontWeight: "800", color: "#0f172a" }}>
                        {inrFormatter.format(req.orderAmount || 0)}
                      </td>
                      <td style={{ padding: "12px 8px" }}>
                        <span
                          style={{
                            fontSize: "11px",
                            fontWeight: 800,
                            padding: "3px 8px",
                            borderRadius: "99px",
                            background: req.status === "APPROVED" ? "#dcfce7" : req.status === "REJECTED" ? "#fee2e2" : "#fef3c7",
                            color: req.status === "APPROVED" ? "#166534" : req.status === "REJECTED" ? "#991b1b" : "#92400e",
                            border: `1px solid ${req.status === "APPROVED" ? "#bbf7d0" : req.status === "REJECTED" ? "#fecaca" : "#fde68a"}`
                          }}
                        >
                          {req.status === "APPROVED" ? "✓ Approved" : req.status === "REJECTED" ? "✕ Rejected" : "● Pending"}
                        </span>
                        {req.status === "REJECTED" && req.rejectionReason && (
                          <span style={{ display: "block", fontSize: "11px", color: "#dc2626", marginTop: "2px" }}>
                            {req.rejectionReason}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: "12px 8px", textAlign: "right" }}>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedRequest(req);
                            setShowRejectBox(false);
                            setRejectionReason("");
                            setReviewModalOpen(true);
                          }}
                          className="btn btn-sm btn-outline-dark"
                          style={{ fontSize: "12px", fontWeight: "800", padding: "5px 12px" }}
                        >
                          Review &amp; Action
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ padding: "40px", textAlign: "center", color: "#64748b", fontSize: "14px" }}>
              No pickup requests found matching this filter.
            </div>
          )}
        </div>

        {/* ═══════════════════════ REVIEW MODAL ═══════════════════════ */}
        {reviewModalOpen && selectedRequest && (
          <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.5)", display: "grid", placeItems: "center", zIndex: 9999, padding: "20px" }}>
            <div style={{ background: "#ffffff", borderRadius: "16px", padding: "26px", maxWidth: "600px", width: "100%", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)", maxHeight: "90vh", overflowY: "auto" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
                <div>
                  <h3 style={{ fontSize: "19px", fontWeight: "900", color: "#0f172a", margin: "0 0 2px" }}>
                    Review Pickup Request
                  </h3>
                  <span style={{ fontSize: "12px", color: "#64748b" }}>ID: {selectedRequest.pickupRequestId}</span>
                </div>
                <button type="button" onClick={() => setReviewModalOpen(false)} style={{ background: "none", border: "none", fontSize: "18px", cursor: "pointer", color: "#64748b" }}>✕</button>
              </div>

              {/* Details Box */}
              <div style={{ background: "#f8fafc", borderRadius: "12px", padding: "16px", marginBottom: "16px", border: "1px solid #edf0f4", fontSize: "13px" }}>
                <div className="row g-2">
                  <div className="col-6">
                    <span style={{ color: "#64748b", fontSize: "11px", fontWeight: "800", textTransform: "uppercase" }}>Order Number</span>
                    <strong style={{ display: "block", color: "#0f172a" }}>#{selectedRequest.orderNumber}</strong>
                  </div>
                  <div className="col-6">
                    <span style={{ color: "#64748b", fontSize: "11px", fontWeight: "800", textTransform: "uppercase" }}>Order Amount</span>
                    <strong style={{ display: "block", color: "#16a34a" }}>{inrFormatter.format(selectedRequest.orderAmount || 0)}</strong>
                  </div>
                  <div className="col-6">
                    <span style={{ color: "#64748b", fontSize: "11px", fontWeight: "800", textTransform: "uppercase" }}>Vendor Store</span>
                    <span style={{ display: "block", color: "#0f172a", fontWeight: "600" }}>{selectedRequest.store?.name || "Vendor Store"}</span>
                  </div>
                  <div className="col-6">
                    <span style={{ color: "#64748b", fontSize: "11px", fontWeight: "800", textTransform: "uppercase" }}>Customer</span>
                    <span style={{ display: "block", color: "#0f172a", fontWeight: "600" }}>{selectedRequest.customerName}</span>
                  </div>
                </div>

                <hr style={{ margin: "12px 0", borderColor: "#edf0f4" }} />

                <span style={{ color: "#64748b", fontSize: "11px", fontWeight: "800", textTransform: "uppercase", display: "block", marginBottom: "4px" }}>
                  Selected Pickup Hub / Warehouse
                </span>
                <div style={{ color: "#0f172a", fontWeight: "700" }}>{selectedRequest.pickupLocationSnapshot?.name}</div>
                <div style={{ color: "#475569", fontSize: "12.5px" }}>
                  📍 {selectedRequest.pickupLocationSnapshot?.street}, {selectedRequest.pickupLocationSnapshot?.city}, {selectedRequest.pickupLocationSnapshot?.state} - {selectedRequest.pickupLocationSnapshot?.pincode}
                </div>
                <div style={{ color: "#475569", fontSize: "12px", marginTop: "2px" }}>
                  👤 Contact: {selectedRequest.pickupLocationSnapshot?.contactPerson} ({selectedRequest.pickupLocationSnapshot?.contactPhone})
                </div>
                {selectedRequest.notes && (
                  <div style={{ marginTop: "8px", padding: "8px", background: "#fef3c7", borderRadius: "6px", fontSize: "12px", color: "#92400e" }}>
                    Vendor Notes: "{selectedRequest.notes}"
                  </div>
                )}
              </div>

              {/* Reject Reason Form */}
              {showRejectBox && (
                <div style={{ marginBottom: "16px" }}>
                  <label style={{ fontSize: "12.5px", fontWeight: "800", color: "#dc2626", display: "block", marginBottom: "4px" }}>
                    Mandatory Rejection Reason *
                  </label>
                  <textarea
                    rows="2"
                    required
                    placeholder="State reason (e.g., location address unreachable, courier unserviceable)..."
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #f87171", fontSize: "13px" }}
                  />
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={() => setReviewModalOpen(false)}
                  className="btn btn-outline-secondary"
                  style={{ padding: "8px 16px", fontSize: "13px", fontWeight: "800" }}
                >
                  Close
                </button>

                {selectedRequest.status !== "APPROVED" && (
                  <>
                    {!showRejectBox ? (
                      <button
                        type="button"
                        onClick={() => setShowRejectBox(true)}
                        className="btn btn-outline-danger"
                        style={{ padding: "8px 16px", fontSize: "13px", fontWeight: "800" }}
                      >
                        ✕ Reject Request
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleReject(selectedRequest._id)}
                        disabled={actionLoading || !rejectionReason.trim()}
                        className="btn btn-danger"
                        style={{ padding: "8px 18px", fontSize: "13px", fontWeight: "800" }}
                      >
                        {actionLoading ? "Rejecting..." : "Confirm Rejection"}
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => handleApprove(selectedRequest._id)}
                      disabled={actionLoading}
                      className="btn btn-success"
                      style={{ padding: "8px 20px", fontSize: "13px", fontWeight: "800" }}
                    >
                      {actionLoading ? "Approving..." : "✓ Approve Pickup Request"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
